import { useState } from 'react';
import { Inbox, PackageCheck, Plus, TriangleAlert } from 'lucide-react';
import type { BackorderRow } from '@eombor/contracts';
import { Badge, Button, Card, EmptyState, Skeleton, cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import type { BackorderTab } from '../../requisition/model/requisitions-search.js';
import { useAuthStore } from '../../auth/model/auth.store.js';
import { usePermission } from '../../auth/model/usePermission.js';
import {
  useBackorderAction,
  useBackorderQueue,
  useMyBackorders,
  usePendingBackorderCount,
  useReadyBackorderCount,
  useReadyBackorders,
} from '../model/backorder.queries.js';
import { AcceptBackorderDialog } from './AcceptBackorderDialog.js';
import { BackorderTable } from './BackorderTable.js';
import { BackorderGroups } from './BackorderGroups.js';
import { groupBackorders, type GroupMode } from '../model/grouping.js';
import { BackorderDialog } from './BackorderDialog.js';
import { FulfilBackorderDialog } from './FulfilBackorderDialog.js';
import {
  anchorOf,
  mergeableWith,
  pickedRows,
  type BackorderSelection,
} from '../model/selection.js';

type Tab = BackorderTab;

/**
 * BILDIRGILAR — uch bo'lim, uch xil savol.
 *
 * Alohida sahifa EMAS, «Talabnomalar» ichidagi ko'rinish: bildirgi —
 * talabnomaning oldingi bosqichi va oxirida talabnomaga aylanadi. Yon
 * menyuda alohida tursa, odam «so'ragan narsam qayerda» deganda ikki
 * bo'lim orasida yurardi.
 *
 * Tartib oqim bo'yicha: «Navbat» (yangi kelgan arizalar), «Kutilmoqda»
 * (qabul qilingan, mahsulot biriktirilishi kerak), «Mening bildirgilarim».
 * Navbatdagi ko'rib chiqilmagan arizalar soni qizil belgi bilan turadi.
 */
export const BackordersView = ({
  tab: tabProp,
  onTabChange,
  onOpenRequisition,
}: {
  /** Manzildan kelgan bo'lim — berilmasa ruxsatga qarab standart tanlanadi. */
  tab?: BackorderTab;
  onTabChange?: (tab: BackorderTab) => void;
  /** Biriktirishdan tug'ilgan talabnomani ochish — yo'lni marshrut biladi. */
  onOpenRequisition?: (requisitionId: string) => void;
} = {}) => {
  const { canAnywhere } = usePermission();
  const canAccept = canAnywhere('backorder.accept');
  const canOversee = canAnywhere('backorder.oversee');
  const canCreate = canAnywhere('backorder.create');
  const myUserId = useAuthStore((state) => state.user?.id ?? null);

  /*
   * STANDART bo'lim — «Navbat».
   *
   * Yangi bildirgi avval navbatga tushadi va «Kutilmoqda» ga faqat qabul
   * qilingandan keyin o'tadi. Boshqa bo'lim birinchi ochilsa, skladchi bo'sh
   * ro'yxatni ko'rib, «bildirgi kelmabdi» degan xulosaga kelardi.
   */
  const [localTab, setLocalTab] = useState<Tab | undefined>(undefined);
  const fallback: Tab = canAccept || canOversee ? 'queue' : 'mine';
  const requested = tabProp ?? localTab ?? fallback;
  /* Manzilda ruxsat berilmagan bo'lim bo'lsa (`?tab=ready` buyurtmachida),
     standartga qaytadi — yashirin bo'limning bo'sh ro'yxati ochilmaydi. */
  const allowed: Record<Tab, boolean> = {
    queue: canAccept || canOversee,
    ready: canAccept,
    mine: true,
  };
  const tab: Tab = allowed[requested] ? requested : fallback;
  const setTab = (next: Tab): void => {
    setLocalTab(next);
    /* Belgilanganlar bo'lim bilan birga TOZALANADI: boshqa ro'yxatga
       o'tib ketgan tanlov ko'rinmay turib tugmani yoqib qo'yardi. */
    setPicked(new Set());
    onTabChange?.(next);
  };
  const readyCount = useReadyBackorderCount(canAccept);
  const pendingCount = usePendingBackorderCount(canAccept);
  const [creating, setCreating] = useState(false);
  const [deciding, setDeciding] = useState<BackorderRow | null>(null);
  const [fulfilling, setFulfilling] = useState<readonly BackorderRow[] | null>(null);
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());

  const mine = useMyBackorders({ limit: 50 });
  const ready = useReadyBackorders({ limit: 50 }, canAccept && tab === 'ready');
  const queue = useBackorderQueue({ limit: 50 }, (canAccept || canOversee) && tab === 'queue');

  const act = useBackorderAction();

  const active = tab === 'mine' ? mine : tab === 'ready' ? ready : queue;
  const rows = active.data?.items ?? [];

  /*
   * GURUHLASH — skladchining navbatida STANDART yoqilgan.
   *
   * «Navbat» va «Kutilmoqda» bo'limida o'nlab ariza yig'iladi va ular
   * yakka-yakka turganda ikkita savol javobsiz qolardi: «bu mahsulotdan
   * jami qancha kerak» (ta'minotga buyurtma aynan shu son bilan
   * beriladi) va «bu uchtasi bitta ish uchunmi». «Mening
   * bildirgilarim» da esa guruhlashning ma'nosi yo'q: u yerda arizalar
   * baribir bitta odamniki va soni kam.
   */
  const [modeByTab, setModeByTab] = useState<Partial<Record<Tab, GroupMode>>>({});
  const mode: GroupMode = modeByTab[tab] ?? (tab === 'mine' ? 'none' : 'product');
  const groups = groupBackorders(rows, mode);

  /*
   * BELGILASH — faqat «Kutilmoqda» da va faqat skladchida.
   *
   * Boshqa bo'limlarda qo'shadigan narsa yo'q: navbatdagi ariza hali
   * qabul qilinmagan, «mening bildirgilarim» esa umuman boshqa odamning
   * ishi. Qoziq — birinchi belgilangan ariza: unga mos kelmaydigan
   * qatorlarning katakchasi o'chadi, chunki talabnomada bitta ombor va
   * bitta buyurtmachi bo'ladi.
   */
  const anchor = anchorOf(rows, picked);
  const chosen = pickedRows(rows, picked);
  const selection: BackorderSelection | undefined =
    canAccept && tab === 'ready'
      ? {
          has: (row) => picked.has(row.id),
          canPick: (row) => mergeableWith(anchor, row),
          toggle: (row) =>
            setPicked((prev) => {
              const next = new Set(prev);
              if (!next.delete(row.id)) next.add(row.id);
              return next;
            }),
        }
      : undefined;

  const tabs: { id: Tab; label: string; shown: boolean; badge?: number }[] = [
    {
      id: 'queue',
      label: t('backorder.tabQueue'),
      shown: allowed.queue,
      badge: pendingCount.data ?? 0,
    },
    {
      id: 'ready',
      label:
        (readyCount.data ?? 0) > 0
          ? `${t('backorder.tabReady')} · ${readyCount.data ?? 0}`
          : t('backorder.tabReady'),
      shown: allowed.ready,
    },
    { id: 'mine', label: t('backorder.tabMine'), shown: allowed.mine },
  ];

  /**
   * Kartochkadagi tugmalar — HOLAT va RUXSATga qarab.
   *
   * Bo'lim bo'yicha emas: bitta bildirgi ikki bo'limda ko'rinishi mumkin
   * (o'z arizasi ham, navbatda ham) va tugmalar ikki joyda ikki xil
   * bo'lsa, odam qaysi biri haqiqiy ekanini bilmasdi.
   */
  const actionsFor = (row: BackorderRow) => ({
    ...(canAccept && row.status === 'SUBMITTED' ? { onAccept: setDeciding } : {}),
    /* Biriktirish qoldiqqa BOG'LANMAGAN: skladchi omborda bor istalgan
       mahsulotni tanlaydi, bog'lanmagan bildirgida esa qoldiq umuman
       hisoblanmaydi. */
    ...(canAccept && row.status === 'ACCEPTED'
      ? { onFulfil: (one: BackorderRow) => setFulfilling([one]) }
      : {}),
    ...(cancellable(row, row.requesterUserId === myUserId, canOversee)
      ? { onCancel: cancel }
      : {}),
  });

  function cancel(row: BackorderRow): void {
    act.mutate({ id: row.id, action: 'cancel' });
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1" role="tablist">
          {tabs
            .filter((item) => item.shown)
            .map((item) => (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={tab === item.id}
                onClick={() => setTab(item.id)}
                className={cn(
                  'rounded-full px-3 py-1.5 text-sm font-medium',
                  'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent',
                  tab === item.id
                    ? 'bg-accent text-accent-contrast'
                    : 'bg-surface-2 text-text-muted hover:text-text',
                )}
              >
                {item.label}
                {item.badge !== undefined && item.badge > 0 ? (
                  <Badge
                    tone="danger"
                    mono
                    className="ml-1.5"
                    aria-label={t('backorder.pendingBadge', { count: item.badge })}
                  >
                    {item.badge}
                  </Badge>
                ) : null}
              </button>
            ))}
        </div>

        {/*
          GURUHLASHNI ALMASHTIRISH — ikki savol, ikki javob.
          Ro'yxat bo'sh bo'lsa chizilmaydi: bo'sh ro'yxatni guruhlash
          degan narsa yo'q.
        */}
        {rows.length === 0 ? null : (
          <div
            role="radiogroup"
            aria-label={t('backorder.groupLabel')}
            className="flex flex-wrap gap-1"
          >
            {(
              [
                { id: 'product', label: t('backorder.groupProduct') },
                { id: 'batch', label: t('backorder.groupBatch') },
                { id: 'none', label: t('backorder.groupNone') },
              ] as const
            ).map((option) => (
              <button
                key={option.id}
                type="button"
                role="radio"
                aria-checked={mode === option.id}
                onClick={() => setModeByTab((prev) => ({ ...prev, [tab]: option.id }))}
                className={cn(
                  'rounded-full border px-2.5 py-1 text-xs font-medium',
                  'transition-colors duration-[var(--motion-fast)]',
                  'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent',
                  mode === option.id
                    ? 'border-accent bg-accent-soft text-accent'
                    : 'border-border bg-surface text-text-muted hover:text-text',
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}

        {canCreate ? (
          <Button className="ml-auto" size="sm" onClick={() => setCreating(true)}>
            <Plus size={14} aria-hidden />
            {t('backorder.new')}
          </Button>
        ) : null}
      </div>

      {/*
        BELGILANGANLAR PANELI — tanlov bo'lgandagina chiziladi.
        Ro'yxatning TEPASIDA: skladchi belgilab bo'lgach ko'zi shu yerga
        qaytadi va tugmani ro'yxatning oxirida qidirmaydi.
      */}
      {chosen.length === 0 ? null : (
        <div
          className={cn(
            'flex flex-wrap items-center gap-3 rounded-[var(--radius-lg)]',
            'border border-accent bg-accent-soft px-3 py-2.5',
          )}
        >
          <span className="text-sm font-medium text-text">
            {t('backorder.pickedCount', { count: chosen.length })}
          </span>
          <span className="min-w-0 flex-1 truncate text-sm text-text-muted">
            {anchor?.requesterName ?? ''}
          </span>
          <Button variant="ghost" size="sm" onClick={() => setPicked(new Set())}>
            {t('backorder.clearPick')}
          </Button>
          <Button size="sm" onClick={() => setFulfilling(chosen)}>
            <PackageCheck size={14} aria-hidden />
            {t('backorder.fulfilTogether')}
          </Button>
        </div>
      )}

      {active.isPending ? (
        <Skeleton className="h-48 w-full" />
      ) : active.isError ? (
        <Card>
          <EmptyState
            icon={<TriangleAlert size={20} aria-hidden />}
            title={t('backorder.loadError')}
            description={active.error.message}
          />
        </Card>
      ) : rows.length === 0 ? (
        <Card>
          <EmptyState
            icon={
              tab === 'ready' ? (
                <PackageCheck size={20} aria-hidden />
              ) : (
                <Inbox size={20} aria-hidden />
              )
            }
            title={tab === 'ready' ? t('backorder.readyEmpty') : t('backorder.empty')}
            description={
              tab === 'ready' ? t('backorder.readyEmptyHint') : t('backorder.emptyHint')
            }
          />
        </Card>
      ) : (
        mode === 'none' ? (
        /* Keng ekranda JADVAL: bildirgi ko'p bo'lganda kartochkalar
           ekranga sig'may ketardi va skladchi ro'yxatni g'ildirak bilan
           kezardi. Telefonda kartochka qoladi — `BackorderTable` o'zi
           tanlaydi. */
        <BackorderTable
          rows={rows}
          /* Sarlavha AKTIV BO'LIM nomi: jadval sarlavhasini ekran
             o'quvchisi o'qiydi va «Bildirgilar» uch bo'limda bir xil
             bo'lsa, qaysi ro'yxat ochilganini aytmasdi. */
          caption={tabs.find((item) => item.id === tab)?.label ?? t('backorder.tabQueue')}
          actionsFor={actionsFor}
          selection={selection}
        />
        ) : (
          <BackorderGroups
            groups={groups}
            actionsFor={actionsFor}
            showProduct={mode !== 'product'}
            selection={selection}
          />
        )
      )}

      {creating ? <BackorderDialog open onClose={() => setCreating(false)} /> : null}
      {deciding === null ? null : (
        <AcceptBackorderDialog row={deciding} onClose={() => setDeciding(null)} />
      )}
      {fulfilling === null ? null : (
        <FulfilBackorderDialog
          rows={fulfilling}
          {...(onOpenRequisition === undefined ? {} : { onFulfilled: onOpenRequisition })}
          onClose={() => {
            setFulfilling(null);
            setPicked(new Set());
          }}
        />
      )}
    </div>
  );
};

/**
 * Bekor qilish kimga ochiq.
 *
 * Egasiga `ACCEPTED` YOPIQ: skladchi allaqachon ish boshlagan va uni
 * xabarsiz to'xtatish mumkin emas. Skladchining o'ziga esa bekor qilish
 * umuman ochilmaydi — uning javobi «rad etish», va u sabab talab qiladi.
 *
 * Qoida domenda ham bor (`canCancel`); bu yerdagi nusxa faqat tugmani
 * yashiradi va server baribir tekshiradi.
 */
const cancellable = (row: BackorderRow, isOwner: boolean, canOversee: boolean): boolean => {
  if (canOversee) return row.status === 'SUBMITTED' || row.status === 'ACCEPTED';
  return isOwner && row.status === 'SUBMITTED';
};
